import type { Mutation } from '@tanstack/react-query';
import { i18n } from '@/i18n';
import { isPlayerAccessDenied } from './playerAccessError';
import { errorStatus as httpStatus, isFetchNetworkError } from './networkError';

/**
 * Mutation `meta` for hooks whose callers report the failure themselves
 * (`mutateAsync` in a try/catch, `onError` passed to `mutate`, `isError` shown on
 * screen): the global toast would say it twice.
 */
export const HANDLES_OWN_ERROR = { handlesOwnError: true } as const;

/**
 * Mutation `meta` for hooks whose screens leave the failure to the global
 * toast. Every `useMutation` declares one or the other (guarded by a test):
 * a new hook forgetting it would say a failure twice, or not at all.
 */
export const USES_GLOBAL_ERROR_TOAST = { handlesOwnError: false } as const;

type AnyMutation = Mutation<unknown, unknown, unknown, unknown>;

// Errors are recognised by name and status rather than `instanceof`: importing
// `services/http` here would close an import cycle through the auth store.
// Not `instanceof Error`: a `DOMException` (abort) is not one in every engine.
function errorName(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('name' in error)) return undefined;
  return typeof error.name === 'string' ? error.name : undefined;
}

/** Whether the global toast should report this failed mutation. */
export function shouldToastMutationError(error: unknown, mutation: AnyMutation): boolean {
  if (mutation.options.onError) return false;
  if (mutation.meta?.handlesOwnError === true) return false;
  // Has its own toast; an expired session redirects to sign in; an abort is the caller's choice.
  if (isPlayerAccessDenied(error)) return false;
  // An outdated app (VOL-14): the "update required" screen already says it.
  if (httpStatus(error) === 426) return false;
  const name = errorName(error);
  return name !== 'SessionExpiredError' && name !== 'AbortError';
}

/** Translated message for a failed mutation nobody else reported. */
export function mutationErrorMessage(error: unknown): string {
  // Already translated, and tells a write to check before retrying.
  if (error instanceof Error && error.name === 'RequestTimeoutError') return error.message;
  const status = httpStatus(error);
  if (status !== undefined) {
    if (status >= 500) return i18n.t('common:errors.http.server_error');
    if (status === 400) return i18n.t('common:errors.http.bad_request');
    if (status === 403) return i18n.t('common:errors.http.forbidden');
    if (status === 404) return i18n.t('common:errors.http.not_found');
    if (status === 409) return i18n.t('common:errors.http.conflict');
    if (status === 413) return i18n.t('common:errors.http.payload_too_large');
    if (status === 429) return i18n.t('common:errors.http.too_many_requests');
    return i18n.t('common:errors.generic');
  }
  if (isFetchNetworkError(error)) return i18n.t('common:errors.http.network');
  return i18n.t('common:errors.generic');
}

/**
 * Message for a screen that reports its own failure: the backend's business
 * message when it sent one, otherwise the translated one — never a raw
 * « HTTP 502 » or the browser's English « Failed to fetch ».
 */
export function readableErrorMessage(error: unknown): string {
  const locked = lockedTeamMessage(error);
  if (locked) return locked;
  // A 5xx carries no business message: NestJS answers « Internal server error » in English.
  const status = httpStatus(error);
  // A 413 is answered by the HTTP layer before NestJS, a 429 by the throttler: English both.
  const isRaw =
    !(error instanceof Error) ||
    error instanceof TypeError ||
    !error.message ||
    /^HTTP \d{3}$/.test(error.message) ||
    (status !== undefined && (status >= 500 || status === 413 || status === 429));
  return isRaw ? mutationErrorMessage(error) : error.message;
}

/**
 * Locked team: the API message is French and tells everyone to reactivate the
 * team, which only its payer's side can do (`viewerCanReactivate`, VOL-199).
 * Read from the body by shape, for the same import cycle as above.
 */
function lockedTeamMessage(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('body' in error)) return undefined;
  const { body } = error;
  if (typeof body !== 'object' || body === null || !('code' in body) || body.code !== 'TEAM_LOCKED') {
    return undefined;
  }
  const asksPayer = 'viewerCanReactivate' in body && body.viewerCanReactivate === false;
  return i18n.t(asksPayer ? 'common:errors.team_locked_ask_payer' : 'common:errors.team_locked');
}
